import { moodKeyToLegacyLabel, normalizeMoodKey } from './moodOptions';

export const MOOD_COLORS: Record<string, string> = {
  happy: '#FFB7D5',
  calm: '#B8E8F2',
  focused: '#B8E6AE',
  satisfied: '#FFE7A3',
  tired: '#C8C4E8',
  anxious: '#FFCAA6',
  bored: '#D8D0C4',
  down: '#98B8F4',
};

export const MOOD_GLASS_BUTTON_CLASS =
  'app-caption app-glass-button inline-flex items-center justify-center rounded-full border px-3 py-1.5 shadow-sm transition-colors';

function hashStringToInt(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function hslToHex(h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16) / 255;
  const g = parseInt(cleaned.substring(2, 4), 16) / 255;
  const b = parseInt(cleaned.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, l };
}

function softenHexColor(hex: string): string {
  try {
    const { h, s, l } = hexToHsl(hex);
    const s2 = Math.max(0, Math.min(1, s * 0.75)); // 降低饱和度
    const l2 = Math.max(0, Math.min(1, l + 0.06)); // 提升亮度
    return hslToHex(h, s2, l2);
  } catch {
    return hex;
  }
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function withHexAlpha(hex: string, alpha: number): string {
  const cleaned = hex.replace('#', '');
  const normalized = cleaned.length === 3
    ? cleaned.split('').map((ch) => `${ch}${ch}`).join('')
    : cleaned;
  if (normalized.length !== 6) return hex;
  const alphaHex = Math.round(clamp01(alpha) * 255).toString(16).padStart(2, '0');
  return `#${normalized}${alphaHex}`;
}

export function getMoodTextColor(label: string | undefined): string | undefined {
  const base = getMoodColor(label);
  if (!base) return undefined;
  try {
    const { h, s, l } = hexToHsl(base);
    const strongerS = Math.max(0.52, Math.min(1, s * 1.18));
    const darkerL = Math.max(0.26, Math.min(0.42, l - 0.28));
    return hslToHex(h, strongerS, darkerL);
  } catch {
    return base;
  }
}

export function getMoodGlassStyle(label: string | undefined): {
  background: string;
  border: string;
  boxShadow: string;
  color: string;
} | undefined {
  const base = getMoodColor(label);
  const text = getMoodTextColor(label);
  if (!base || !text) return undefined;
  const highlight = softenHexColor(base);
  return {
    background: `linear-gradient(135deg, ${withHexAlpha(highlight, 0.88)} 0%, ${withHexAlpha(base, 0.78)} 52%, ${withHexAlpha(base, 0.9)} 100%) padding-box, linear-gradient(140deg, ${withHexAlpha(base, 0.32)} 0%, ${withHexAlpha(highlight, 0.7)} 56%, ${withHexAlpha('#FFFFFF', 0.82)} 100%) border-box`,
    border: '0.5px solid transparent',
    boxShadow: '0 6px 14px rgba(165,190,103,0.14)',
    color: text,
  };
}

export function getMoodColor(label: string | undefined): string | undefined {
  if (!label) return undefined;
  const normalizedKey = normalizeMoodKey(label);
  const paletteKey = normalizedKey || label;
  if (MOOD_COLORS[paletteKey]) return softenHexColor(MOOD_COLORS[paletteKey]);
  if (normalizedKey) {
    const legacyLabel = moodKeyToLegacyLabel(normalizedKey);
    if (MOOD_COLORS[legacyLabel]) return softenHexColor(MOOD_COLORS[legacyLabel]);
  }
  let hue = hashStringToInt(label) % 360;
  // 更低饱和、更高亮度的粉彩色
  let color = hslToHex(hue, 0.42, 0.84);
  const used = new Set(Object.values(MOOD_COLORS).map(c => c.toLowerCase()));
  let tries = 0;
  while (used.has(color.toLowerCase()) && tries < 12) {
    hue = (hue + 30) % 360;
    color = hslToHex(hue, 0.42, 0.84);
    tries++;
  }
  return softenHexColor(color);
}
